/**
 * Central formatting utilities — Portuguese locale (pt-PT).
 *
 * All date/time/currency output in the app should go through these helpers
 * so locale changes are made in one place.
 *
 * Date format rules (CLAUDE.md):
 *   - Full date:         DD/MM/YYYY
 *   - Short date:        DD/MM
 *   - Date + time:       DD/MM/YYYY HH:mm
 *   - Date + time + sec: DD/MM/YYYY HH:mm:ss
 *   - Month + year:      localised (e.g. "abril de 2026")
 *   - Weekday + date:    localised (e.g. "qua., 15/04")
 */

import { format as dfFormat, isValid, parseISO } from 'date-fns';
import { pt } from 'date-fns/locale';

const PT = pt; // date-fns Portuguese locale

// ── Date-only helpers ──────────────────────────────────────────────────────

/** DD/MM/YYYY — standard full date */
export function fmtDate(d: Date | string | null | undefined): string {
  const date = toDate(d);
  if (!date) return '—';
  return dfFormat(date, 'dd/MM/yyyy');
}

/** DD/MM — short date without year */
export function fmtDateShort(d: Date | string | null | undefined): string {
  const date = toDate(d);
  if (!date) return '—';
  return dfFormat(date, 'dd/MM');
}

// ── Date + time helpers ───────────────────────────────────────────────────

/** DD/MM/YYYY HH:mm */
export function fmtDateTime(d: Date | string | null | undefined): string {
  const date = toDate(d);
  if (!date) return '—';
  return dfFormat(date, 'dd/MM/yyyy HH:mm');
}

/** DD/MM/YYYY HH:mm:ss */
export function fmtDateTimeSec(d: Date | string | null | undefined): string {
  const date = toDate(d);
  if (!date) return '—';
  return dfFormat(date, 'dd/MM/yyyy HH:mm:ss');
}

/** HH:mm */
export function fmtTime(d: Date | string | null | undefined): string {
  const date = toDate(d);
  if (!date) return '—';
  return dfFormat(date, 'HH:mm');
}

// ── Calendar / narrative helpers ──────────────────────────────────────────

/** "abril de 2026" — month name + year for calendar headers */
export function fmtMonthYear(d: Date | string | null | undefined): string {
  const date = toDate(d);
  if (!date) return '—';
  return dfFormat(date, 'MMMM yyyy', { locale: PT });
}

/** "qua., 15/04" — weekday abbreviation + short date */
export function fmtWeekdayShort(d: Date | string | null | undefined): string {
  const date = toDate(d);
  if (!date) return '—';
  return dfFormat(date, 'EEE, dd/MM', { locale: PT });
}

/** "quarta-feira, 15 de abril de 2026" — long localised date */
export function fmtDateLong(d: Date | string | null | undefined): string {
  const date = toDate(d);
  if (!date) return '—';
  return dfFormat(date, 'EEEE, dd/MM/yyyy', { locale: PT });
}

// ── Currency helper ───────────────────────────────────────────────────────

/** Format as EUR using pt-PT locale: "1 234,56 €" */
export function fmtEur(amount: number, currency = 'EUR'): string {
  return amount.toLocaleString('pt-PT', { style: 'currency', currency });
}

// ── Number helper ─────────────────────────────────────────────────────────

/** pt-PT thousands separator: 1 234 */
export function fmtNumber(n: number): string {
  return n.toLocaleString('pt-PT');
}

// ── Internal ──────────────────────────────────────────────────────────────

function toDate(d: Date | string | null | undefined): Date | null {
  if (!d) return null;
  if (d instanceof Date) return isValid(d) ? d : null;
  const parsed = parseISO(d);
  return isValid(parsed) ? parsed : null;
}
